import React, { useEffect, useState } from 'react';

import { FormikValues } from 'formik';

import {
  adminActions,
  adminBookedPharmacistDetails,
  adminBookingDetails,
  AdminLegendVariant,
  adminCancelBooking,
  adminRequestCancellation,
  adminResendEmail,
  adminResetValue,
  ConfirmationTypes,
  getRequestCancellation,
  PermissionsOfAdmin,
  PermissionsRemovalKeys,
  resetStatus,
  selectRequestCancellation,
  ServiceTypes,
  successSelector,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { renderScreen } from '../../actions/drawerActions';
import { OutcomeModalTypes, ScreenTypes } from '../../helpers/Constants';
import InputModal from '../../components/Modals/InputModal';
import { setDialog } from '../../reducers/dialogReducer';
import styles from '../../components/Admin/RequestedPharmacistList/PharmacistList/styles';
import {
  cancellationForm,
  resendEmailForm,
} from '../../components/Admin/BookingList/helper';
import {
  getDayMonthDateYear,
  getHourMinute,
} from '../../components/Reports/helpers';
import OutcomeModal from '../../components/Modals/OutcomeModal';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import strings from '../../localization';

import { genericFunction } from './useAvailableWorkshiftActions';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';

import Validator from './Validator';
import { serverToday } from '../../helpers/Functions';
import useAdminPermissions from '../Admin/useAdminPermissions';
import { removeBasedOnPermissions } from '../../helpers/permissionsHelper';
import { showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import ResendEmailValidator from './ResendEmailValidator';
import useCancelMultipleBooking from '../useCancelMultipleBooking';
import ViewPharmacyShiftDetails from '../../components/Admin/BookingList/ViewPharmacyShiftDetails';

const keysToRemove = {
  requestCancellation: 'requestCancellation',
  resendEmail: 'resendEmail',
  viewPharmacyShiftDetails: 'viewPharmacyShiftDetails',
};

const criteria = [keysToRemove.requestCancellation, keysToRemove.resendEmail];
const viewDetailsKey = '1';
// Past bookings can only be viewed, and super admins can also edit them.
const pastBookingMenuKeys = [viewDetailsKey];
const editablePastBookingMenuKeys = [viewDetailsKey, PermissionsRemovalKeys.ExpressBooking];
const isSelfService = ServiceHandler.getService() === ServiceTypes.self;
interface ICancellationInitialValues {
  startDate: string;
  endDate: string;
  pharmacyName: string;
}

interface IActions {
  id: string;
  pharmacyId: string;
  additionalParams: ICancellationInitialValues;
  longTermShiftContractId?: string;
  professionalId?: string;
}

export const menuActions = (
  requestCancellation: genericFunction,
  editAction: genericFunction,
  viewPharmacyShiftDetails: genericFunction,
  viewDetailsAction: genericFunction,
  resendEmail: genericFunction,
) =>
  [
    {
      key: viewDetailsKey,
      label: strings.viewDetails,
      onClick: viewDetailsAction,
    },
    {
      key: keysToRemove.viewPharmacyShiftDetails,
      label: strings.viewPharmacyShiftDetails,
      onClick: viewPharmacyShiftDetails,
    },
    {
      key: PermissionsRemovalKeys.ExpressBooking,
      label: strings.edit,
      onClick: editAction,
    },
    {
      key: keysToRemove.requestCancellation,
      label: isSelfService ? strings.cancelBooking : strings.requestCancellation,
      onClick: requestCancellation,
    },
    {
      key: keysToRemove.resendEmail,
      label: strings.resendEmail,
      onClick: resendEmail,
    },
  ];

const useBookedWorkshiftActions = (
  goToBookingList: () => void,
  isAutoSyncRecord?: boolean,
  externalPharmacistId?: string,
) => {
  const { showCancellationDialog } = useCancelMultipleBooking();
  const [bookingID, setBookingID] = useState('');
  const [isResendEmail, setIsResendEmail] = useState(false);
  const dispatch = useAppDispatch();
  const today = serverToday().toISO({ includeOffset: false }) ?? '';
  const { can, canEditPastBookings } = useAdminPermissions();

  const successAction = useAppSelector((state) =>
    successSelector([adminActions.getBookedPharmacistDetails], state));

  const emailResendSuccess = useAppSelector((state) =>
    successSelector([adminActions.adminResendEmail], state));

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const cancellationData = useAppSelector(selectRequestCancellation);
  const bookingDetails = useAppSelector(adminBookingDetails);

  const { startDate, endDate, pharmacyName, success, message } = cancellationData ?? {};
  const {
    startDate: bookingStartDate,
    endDate: bookingEndDate,
    pharmacyName: bookingPharmacyName,
    firstName,
    lastName,
  } = bookingDetails ?? {};

  const pharmacistName = `${firstName} ${lastName}`;

  const goToViewDetails = (bookingId: string, pharmacyId: string) =>
    () => {
      dispatch(
        renderScreen({
          screenNumber: 3,
          screenType: ScreenTypes.availableWorkshift,
          eventId: bookingId,
          type: AdminLegendVariant.booked,
          back: goToBookingList,
          pharmacyId,
          extraParameters: {
            isAutoSyncRecord,
            externalPharmacistId,
          },
        }),
      );
    };

  const requestCancellation = (bookingId: string) =>
    (values: FormikValues) => {
      const { reason } = values ?? {};
      dispatch(adminRequestCancellation({ reason, bookingId }));
    };

  const resendEmailSubmit = (bookingId: string) =>
    (values: FormikValues) => {
      const { sendEmailToPharmacy, sendEmailToPharmacist } = values ?? {};
      dispatch(
        adminResendEmail({
          sendEmailToPharmacist,
          sendEmailToPharmacy,
          bookingId,
        }),
      );
    };

  const showRequestCancellationForm = (bookingId: string) => {
    const date = getDayMonthDateYear(startDate);
    const time = `${getHourMinute(startDate, timeFormat)}-${getHourMinute(
      endDate,
      timeFormat,
    )}`;

    dispatch(
      setDialog({
        Component: (
          <InputModal
            onSubmit={requestCancellation(bookingId)}
            initialValues={{
              date,
              time,
              pharmacyName,
              reason: '',
            }}
            submitLabel={strings.submit}
            config={cancellationForm()}
            validator={Validator()}
          />
        ),
        heading: {
          title: strings.requestCancellation,
        },
        customStyle: {
          customTitleContainer: styles.modalMargin,
        },
        showCloseButton: true,
      }),
    );
  };

  const showResendEmail = (bookingId: string) => {
    const date = getDayMonthDateYear(bookingStartDate);
    const time = `${getHourMinute(
      bookingStartDate,
      timeFormat,
    )}-${getHourMinute(bookingEndDate, timeFormat)}`;

    dispatch(
      setDialog({
        Component: (
          <InputModal
            onSubmit={resendEmailSubmit(bookingId)}
            initialValues={{
              date,
              time,
              pharmacyName: bookingPharmacyName,
              pharmacistName,
              sendEmailToPharmacist: true,
              sendEmailToPharmacy: true,
            }}
            submitLabel={strings.submit}
            config={resendEmailForm()}
            validator={ResendEmailValidator()}
          />
        ),
        heading: {
          title: strings.resendEmail,
        },
        customStyle: {
          customTitleContainer: styles.modalMargin,
        },
        showCloseButton: true,
      }),
    );
  };
  const viewMultiShiftDetails = (
    longTermContractId: string,
    professionalId: string,
  ) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <ViewPharmacyShiftDetails
              longTermContractId={longTermContractId}
              professionalId={professionalId}
            />
          ),
          heading: {
            title: strings.shiftDetails,
          },
          showCloseButton: true,
          maxWidth: 'lg',
        }),
      );
    };

  const showAlreadyRequestCancelled = () => {
    dispatch(
      setDialog({
        Component: (
          <OutcomeModal
            type={OutcomeModalTypes.submitSuccess}
            message={message}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: true,
      }),
    );
  };

  const goToMdsCancellation = ({
    longTermShiftContractId,
    professionalId,
  }: {
    longTermShiftContractId: string;
    professionalId?: string;
  }) =>
    () => {
      showCancellationDialog({ longTermShiftContractId, professionalId });
    };

  const goToRequestedCancellation = (bookingId: string) =>
    () => {
      dispatch(getRequestCancellation(bookingId));
      setBookingID(bookingId);
    };

  const confirmSelfServiceCancelBooking = (bookingId: string) =>
    () => {
      dispatch(adminCancelBooking({ bookingId }));
    };

  const cancelSelfServiceBooking = (bookingId: string) =>
    () => {
      dispatch(
        setDialog({
          heading: {
            title: '',
          },
          Component: (
            <ConfirmationModal
              type={ConfirmationTypes.booking}
              confirmAction={confirmSelfServiceCancelBooking(bookingId)}
            />
          ),
          showCloseButton: false,
        }),
      );
    };

  const getCancellationAction = ({
    id,
    longTermShiftContractId,
    professionalId,
  }: Pick<IActions, 'id' | 'longTermShiftContractId' | 'professionalId'>) => {
    if (longTermShiftContractId) {
      return goToMdsCancellation({
        longTermShiftContractId,
        professionalId,
      });
    }

    if (isSelfService) {
      return cancelSelfServiceBooking(id);
    }

    return goToRequestedCancellation(id);
  };

  const resendEmail = (bookingId: string) =>
    () => {
      setBookingID(bookingId);
      dispatch(adminBookedPharmacistDetails({ id: bookingId }));
      setIsResendEmail(true);
    };

  const handleRequestCase = () => {
    if (success) {
      showRequestCancellationForm(bookingID);
    } else {
      showAlreadyRequestCancelled();
    }
    dispatch(adminResetValue(['requestCancellation']));
  };

  useEffect(() => {
    if (success !== null && bookingID) {
      handleRequestCase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, bookingID]);

  useEffect(() => {
    if (bookingID && isResendEmail && successAction) {
      showResendEmail(bookingID);
      setBookingID('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingID, isResendEmail, successAction]);

  useEffect(() => {
    if (emailResendSuccess) {
      showSuccess(dispatch, strings.emailResentSuccessMsg);
      dispatch(resetStatus([adminActions.adminResendEmail]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailResendSuccess]);

  const editBookingAction = (id: string) =>
    () => {
      dispatch(
        renderScreen({
          screenNumber: 5,
          screenType: ScreenTypes.availableWorkshift,
          eventId: id,
          type: AdminLegendVariant.booked,
        }),
      );
    };

  const actions = ({
    id,
    pharmacyId,
    additionalParams,
    longTermShiftContractId,
    professionalId,
  }: IActions) => {
    const cancellationAction = getCancellationAction({
      id,
      longTermShiftContractId,
      professionalId,
    });

    let menu = menuActions(
      cancellationAction,
      editBookingAction(id),
      viewMultiShiftDetails(longTermShiftContractId ?? '', professionalId ?? ''),
      goToViewDetails(id, pharmacyId),
      resendEmail(id),
    );

    if (additionalParams.startDate < today) {
      const allowedMenuKeys = canEditPastBookings
        ? editablePastBookingMenuKeys
        : pastBookingMenuKeys;
      menu = menu.filter((item) =>
        allowedMenuKeys.includes(item.key));
    }

    menu = removeBasedOnPermissions(menu, [PermissionsOfAdmin.Booking], can);

    if (isAutoSyncRecord) {
      menu = menu.filter((item) =>
        !criteria.includes(item.key));
    }

    if (!longTermShiftContractId) {
      menu = menu.filter((item) =>
        item.key !== keysToRemove.viewPharmacyShiftDetails);
    }

    if (isAutoSyncRecord || !!externalPharmacistId) {
      menu = menu.filter(
        (item) =>
          item.key !== PermissionsRemovalKeys.ExpressBooking,
      );
    }

    return menu;
  };

  return { actions, goToRequestedCancellation };
};

export default useBookedWorkshiftActions;
